import React, { useState, useEffect } from "react";
import { Descriptions, Button, Flex, notification, Input, Modal, Form, InputNumber, DatePicker } from "antd";
import { EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import axiosInstance from "../../../utilities/axios";

const FinancingModal = ({ option, visible, onClose, onChange }) => {
  const [form] = Form.useForm();
  const allValues = Form.useWatch([], form);

  const [editMode, setEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState({});

  useEffect(() => {
    if (!option) return;

    setInitialValues({
      name: option.name,
      term_length: option.term_length,
      interest_rate: option.interest_rate,
      down_payment: option.down_payment,
      start_date: option.start_date ? dayjs(option.start_date) : null,
      end_date: option.end_date ? dayjs(option.end_date) : null,
    });

    form.setFieldsValue({
      name: option.name,
      term_length: option.term_length,
      interest_rate: option.interest_rate,
      down_payment: option.down_payment,
      start_date: option.start_date ? dayjs(option.start_date) : null,
      end_date: option.end_date ? dayjs(option.end_date) : null,
    });
  }, [option])

  const saveChanges = () => {
    const currentValues = form.getFieldsValue();
    const formData = new FormData();

    for (const key in currentValues) {
      if (key === "start_date" || key === "end_date") {
        const currentDate = currentValues[key];
        const initialDate = initialValues[key];
        if (
          (currentDate && initialDate && !currentDate.isSame(initialDate)) ||
          (!currentDate && initialDate) ||
          (currentDate && !initialDate)
        ) {
          formData.append(key, currentDate ? currentDate.format("YYYY-MM-DD") : "");
        }
      } else if (currentValues[key] !== initialValues[key]) {
        formData.append(key, currentValues[key]);
      }
    }

    axiosInstance
      .put(`/financing-options/${option.id}`, formData)
      .then((response) => {
        onChange();
        setEditMode(false);
        onClose();
      })
      .catch((error) => {
        notification.error({
          message: "Error",
          description: error.response?.data?.message || "An error occurred while saving changes.",
        });
      });
  };

  const handleClose = () => {
    setEditMode(false);
    onClose();
    form.resetFields();
  }

  return (
    <Modal
      open={visible}
      title="Financing Option Details"
      width={800}
      onClose={handleClose}
      onCancel={handleClose}
      footer={
        <Button onClick={handleClose}>Cancel</Button>
      }
    >
      <Form
        form={form}
        layout="vertical"
        name="financingForm"
        initialValues={initialValues}
        onFinish={saveChanges}
      >
        <Descriptions
          bordered
          column={4}
        >
          <Descriptions.Item
            label="Name"
            span={4}
          >
            {editMode ? (
              <Form.Item
                name="name"
                rules={[{ required: true, message: "Please enter a name" }]}
                style={{ marginBottom: 0 }}
              >
                <Input style={{ width: "100%" }} />
              </Form.Item>
            ) : (
              initialValues.name || "No name available"
            )}
          </Descriptions.Item>
          <Descriptions.Item
            label="Term Length"
            span={4}
          >
            {editMode ? (
              <Form.Item
                name="term_length"
                rules={[{ required: true, message: "Please enter a term length" }]}
                style={{ marginBottom: 0 }}
              >
                <InputNumber style={{ width: "100%" }} suffix="months" />
              </Form.Item>
            ) : (
              initialValues.term_length + " months" || "No term length available"
            )}
          </Descriptions.Item>
          <Descriptions.Item
            label="Interest Rate"
            span={4}
          >
            {editMode ? (
              <Form.Item
                name="interest_rate"
                rules={[{ required: true, message: "Please enter an interest rate" }]}
                style={{ marginBottom: 0 }}
              >
                <InputNumber style={{ width: "100%" }} suffix="%" />
              </Form.Item>
            ) : (
              (initialValues.interest_rate !== undefined && initialValues.interest_rate !== null
                ? Number(initialValues.interest_rate).toFixed(2) + "%"
                : "No interest rate available")
            )}
          </Descriptions.Item>
          <Descriptions.Item
            label="Minimum Down Payment"
            span={4}
          >
            {editMode ? (
              <Form.Item
                name="down_payment"
                rules={[{ required: true, message: "Please enter a down payment" }]}
                style={{ marginBottom: 0 }}
              >
                <InputNumber style={{ width: "100%" }} suffix="%" />
              </Form.Item>
            ) : (
              (initialValues.down_payment !== undefined && initialValues.down_payment !== null
                ? Number(initialValues.down_payment).toFixed(2) + "%"
                : "No down payment available")
            )}
          </Descriptions.Item>
          <Descriptions.Item
            label="Start Date"
            span={2}
          >
            {editMode ? (
              <Form.Item
                name="start_date"
                rules={[{ required: true, message: "Please select a start date" }]}
                style={{ marginBottom: 0 }}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            ) : (
              initialValues.start_date ? dayjs(initialValues.start_date).format("YYYY-MM-DD") : "No start date available"
            )}
          </Descriptions.Item>
          <Descriptions.Item
            label="End Date"
            span={2}
          >
            {editMode ? (
              <Form.Item
                name="end_date"
                style={{ marginBottom: 0 }}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            ) : (
              initialValues.end_date ? dayjs(initialValues.end_date).format("YYYY-MM-DD") : "No end date available"
            )}
          </Descriptions.Item>
        </Descriptions>
        <Flex
          justify="flex-end"
          style={{ marginTop: "10px", gap: "10px" }}
        >
          <Button
            key="save"
            htmlType="submit"
            type="primary"
            disabled={
              editMode &&
              (allValues.name === initialValues.name &&
              allValues.term_length === initialValues.term_length &&
              allValues.interest_rate === initialValues.interest_rate &&
              allValues.down_payment === initialValues.down_payment &&
              allValues.start_date.isSame(initialValues.start_date) &&
              (allValues.end_date === null && initialValues.end_date === null ?
                true : allValues.end_date?.isSame(initialValues.end_date)))
            }
            style={{ display: editMode ? "block" : "none" }}
          >
            Save Changes
          </Button>
          <Button
            key="edit"
            type={editMode ? "dashed" : "text"}
            icon={<EditOutlined />}
            onClick={() => {
              if (editMode) {
                form.resetFields();
                form.setFieldsValue(initialValues);
              }
              setEditMode(!editMode);
            }}
            style={{ display: initialValues.status === "discontinued" ? "none" : "block" }}
          />
        </Flex>
      </Form>
    </Modal>
  )
};

export default FinancingModal;
